import {
  AXIS_LABEL_COLOR,
  AXIS_LINE_COLOR,
  CHART_AXIS_TITLE_FONT_SIZE,
  GRID_LINE_COLOR,
} from "../constants";
import { DateRangeEntry } from "../types";
import { formatAnalyticsNumber } from "../utils/formatAnalyticsNumber";

import { formatSpacedCategoryAxisLabel } from "@/utils/dateAxisCategoryLabels";

export const buildBottomDateAxis = (dateEntries: DateRangeEntry[] = []) => {
  const labelsByKey = new Map(dateEntries.map(({ key, label }) => [key, label]));

  return {
    type: "category" as const,
    position: "bottom" as const,
    label: {
      fontSize: CHART_AXIS_TITLE_FONT_SIZE,
      color: AXIS_LABEL_COLOR,
      formatter: ({ value, index }: { value: string | number; index: number }) =>
        formatSpacedCategoryAxisLabel({
          datapointCount: dateEntries.length,
          value: labelsByKey.get(String(value)) ?? String(value),
          index,
        }),
      avoidCollisions: true,
      wrapping: "never" as const,
    },
    line: {
      stroke: AXIS_LINE_COLOR,
    },
    tick: {
      stroke: AXIS_LINE_COLOR,
    },
    crosshair: {
      enabled: false,
      label: {
        enabled: false,
      },
    },
  };
};

export const bottomDateAxis = buildBottomDateAxis();

export const leftCategoryAxis = {
  type: "category" as const,
  position: "left" as const,
  label: {
    fontSize: CHART_AXIS_TITLE_FONT_SIZE,
    color: AXIS_LABEL_COLOR,
    wrapping: "never" as const,
    truncate: true,
  },
  line: {
    stroke: AXIS_LINE_COLOR,
  },
  tick: {
    stroke: AXIS_LINE_COLOR,
  },
  crosshair: {
    enabled: false,
    label: {
      enabled: false,
    },
  },
};

/** Category axis for vertical bar charts (ranked lists, labels on X) */
export const bottomCategoryAxis = {
  type: "category" as const,
  position: "bottom" as const,
  label: {
    fontSize: CHART_AXIS_TITLE_FONT_SIZE,
    color: AXIS_LABEL_COLOR,
    wrapping: "never" as const,
    truncate: true,
    avoidCollisions: true,
  },
  line: {
    stroke: AXIS_LINE_COLOR,
  },
  tick: {
    stroke: AXIS_LINE_COLOR,
  },
  crosshair: {
    enabled: false,
    label: {
      enabled: false,
    },
  },
};

export const numberAxis = ({
  keys,
  position = "left",
  title,
  formatValue = formatAnalyticsNumber,
  min,
  max,
}: {
  keys: string[];
  position?: "left" | "right" | "bottom";
  title: string;
  formatValue?: (value: number) => string;
  min?: number;
  max?: number;
}) => ({
  type: "number" as const,
  position,
  keys,
  ...(min !== undefined ? { min } : {}),
  ...(max !== undefined ? { max } : {}),
  label: {
    fontSize: CHART_AXIS_TITLE_FONT_SIZE,
    color: AXIS_LABEL_COLOR,
    formatter: ({ value }: { value: number | string }) => formatValue(Number(value)),
  },
  title: {
    enabled: true,
    text: title,
    fontSize: CHART_AXIS_TITLE_FONT_SIZE,
    color: AXIS_LABEL_COLOR,
  },
  line: {
    stroke: AXIS_LINE_COLOR,
  },
  tick: {
    stroke: AXIS_LINE_COLOR,
  },
  gridLine: {
    enabled: position !== "right",
    style: [
      {
        stroke: GRID_LINE_COLOR,
        lineDash: [0],
      },
    ],
  },
  crosshair: {
    enabled: false,
    label: {
      enabled: false,
    },
  },
});
